import { Injectable } from '@angular/core';
import { Observable, Subject, defer, firstValueFrom, map, of, shareReplay } from 'rxjs';

import {
  FINANCE_ANALYTIC_DISTRIBUTION_MODELS_ENDPOINT,
  FINANCE_ANALYTIC_DISTRIBUTION_MODELS_OPTIONS_ENDPOINT,
  FINANCE_POST_BUDGET_AVAILABILITY_ENDPOINT,
} from '../api/api-endpoints';
import { RestApiService } from '../api/rest-api.service';
import { TokenService } from './token.service';

export interface AnalyticDistributionOwnerOption {
  id: string;
  label: string;
  code?: string | null;
  analytic_plan_id?: string | null;
}

export interface AnalyticDistributionOptions {
  analytic_accounts: AnalyticDistributionOwnerOption[];
  partners: AnalyticDistributionOwnerOption[];
  products: AnalyticDistributionOwnerOption[];
  product_categories: AnalyticDistributionOwnerOption[];
}

export interface AnalyticDistributionModelRecord {
  model: {
    id: string;
    name: string;
    partner_id?: string | null;
    product_id?: string | null;
    product_category_id?: string | null;
    account_prefix?: string | null;
    active: boolean;
    sequence: number;
  };
  lines: Array<{ analytic_account_id: string; percentage: string | number }>;
}

export interface AnalyticBudgetAvailability {
  allowed: boolean;
  checks: Array<Record<string, unknown>>;
  exceeded: Array<{
    budget_name?: string;
    analytic_account_name?: string;
    remaining_amount?: number | string;
    requested_amount?: number | string;
  }>;
}

@Injectable({ providedIn: 'root' })
export class AnalyticDistributionService {
  private optionsRequest?: Observable<AnalyticDistributionOptions>;
  private modelsRequest?: Observable<{ records: AnalyticDistributionModelRecord[] }>;
  private modelRecords: AnalyticDistributionModelRecord[] | null = null;
  private sessionToken: string | null | undefined;
  private cacheEpoch = 0;
  private readonly modelChangesSubject = new Subject<AnalyticDistributionModelRecord>();

  readonly modelChanges$ = this.modelChangesSubject.asObservable();

  constructor(private readonly restApi: RestApiService, private readonly tokens: TokenService) {}

  options(): Observable<AnalyticDistributionOptions> {
    this.ensureSession();
    const epoch = this.cacheEpoch;
    const token = this.sessionToken;
    this.optionsRequest ??= this.restApi
      .get<AnalyticDistributionOptions>(FINANCE_ANALYTIC_DISTRIBUTION_MODELS_OPTIONS_ENDPOINT)
      .pipe(
        map(response => this.isCurrent(epoch, token) ? response : {
          analytic_accounts: [], partners: [], products: [], product_categories: [],
        }),
        shareReplay(1),
      );
    return this.optionsRequest;
  }

  models(): Observable<{ records: AnalyticDistributionModelRecord[] }> {
    this.ensureSession();
    if (this.modelRecords) {
      return of({ records: [...this.modelRecords] });
    }
    const epoch = this.cacheEpoch;
    const token = this.sessionToken;
    this.modelsRequest ??= defer(() => this.loadModels(epoch, token)).pipe(shareReplay(1));
    return this.modelsRequest;
  }

  upsertModel(record: AnalyticDistributionModelRecord): void {
    this.ensureSession();
    // A single inline save is not proof that the complete catalogue was loaded.
    if (this.modelRecords !== null) {
      this.modelRecords = [...this.modelRecords.filter(candidate => candidate.model.id !== record.model.id), record];
    }
    this.cacheEpoch++;
    this.optionsRequest = undefined;
    this.modelsRequest = undefined;
    this.modelChangesSubject.next(record);
  }

  checkBudgetAvailability(
    analyticDistribution: Record<string, number>,
    amount: number,
    date?: string | null,
  ): Observable<AnalyticBudgetAvailability> {
    return this.restApi.post<AnalyticBudgetAvailability>(FINANCE_POST_BUDGET_AVAILABILITY_ENDPOINT, {
      analytic_distribution: analyticDistribution,
      amount,
      date: date || null,
    });
  }

  invalidate(): void {
    this.cacheEpoch++;
    this.optionsRequest = undefined;
    this.modelsRequest = undefined;
    this.modelRecords = null;
  }

  private ensureSession(): void {
    const token = this.tokens.getAccessToken();
    if (token === this.sessionToken) return;
    this.invalidate();
    this.sessionToken = token;
  }

  private isCurrent(epoch: number, token: string | null | undefined): boolean {
    return epoch === this.cacheEpoch && token === this.tokens.getAccessToken();
  }

  private async loadModels(epoch: number, token: string | null | undefined): Promise<{ records: AnalyticDistributionModelRecord[] }> {
    const records: AnalyticDistributionModelRecord[] = [];
    for (let page = 1; page <= 100; page++) {
      const response = await firstValueFrom(this.restApi.get<{
        records: AnalyticDistributionModelRecord[];
        pagination?: { total?: number };
      }>(`${FINANCE_ANALYTIC_DISTRIBUTION_MODELS_ENDPOINT}?page=${page}&limit=100`));
      if (!this.isCurrent(epoch, token)) return { records: [] };
      const rows = response.records ?? [];
      records.push(...rows);
      const total = response.pagination?.total;
      if (rows.length === 0 || (typeof total === 'number' ? records.length >= total : rows.length < 100)) {
        this.modelRecords = [...records];
        return { records };
      }
    }
    throw new Error('The analytic model catalogue is too large to load safely.');
  }
}
